import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import styled, { css, keyframes } from 'styled-components';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import { colors } from '../../constants/colors';
import FileUpload from '../SolvPatternLibrary/FileUpload';

const BaseCard = styled(Card)`
  width: 328px;
  height: 192px;
  border-radius: 12px;
  position: relative;
  box-shadow: none;
`;

const StyledCard = styled(BaseCard)`
  text-align: center;
  overflow: visible;
  margin-top: 15px;
  cursor: pointer;
  box-shadow: none;
  ${props => css`
    border: 1px ${props.sc.borderStyle} ${props.sc.borderColor || colors.black};
    opacity: ${props.sc.disabled ? 0.5 : 1};
  `};
`;

const StyledCardPicture = styled(BaseCard)`
  background: ${colors.white} url(${props => props.sc.imgData}) no-repeat center;
  background-size: cover;
  overflow: hidden;
  -webkit-mask-image: -webkit-radial-gradient(white, black);
`;

const scanKeyframe = keyframes`
  from { transform: translateX(-328px); }
  to { transform: translateX(0); }
`;

const scanAnimation = css`
  animation: ${scanKeyframe} 2s linear infinite alternate;
`;

const StyledScanOverlay = styled(BaseCard)`
  border-radius: 0;
  background-color: ${colors.blueJayWay};
  opacity: 0.5;
  transform: translateX(-328px);
  ${({ sc }) => sc.selfAnimate && scanAnimation}
`;

const StyledScanHead = styled.div`
  height: 210px;
  z-index: 1;
  box-shadow: 0 0 2px ${colors.greyHounds};
  width: 5px;
  position: absolute;
  transform: translateX(-328px);
  right: -2.5px;
  top: -9px;
  background-color: ${colors.white};
  margin: auto 0;
  ${({ sc }) => sc.selfAnimate && scanAnimation}
`;

const StyledCardHeader = styled(Typography)`
  color: ${colors.fadeToBlack};
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 0.38px;
  line-height: 27px;
`;

const StyledCardBody = styled(Typography)`
  color: ${colors.blueMonday};
  font-size: 14px;
  letter-spacing: -0.2px;
  line-height: 20px;
`;

const UploadButton = styled(Button)`
  width: 80% !important;
  height: 45px;
  padding: 0;
  margin-top: 20px;
`;

const StyledInput = styled(FileUpload)`
  width: 328px;
  height: 192px;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0.01;
  border: none;
  z-index: 10;
`;

const HintContainer = styled.div`
  text-align: left;
  margin-left: 20px;
`;

const Container = styled.div`
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  width: 90%;
  margin-top: 20px;
`;

const CardPlaceholder = styled.img`
  height: 58px;
  width: 43px;
`;

const VIEW_PICTURE = 0;
const VIEW_PLACEHOLDER = 1;
const VIEW_BLANK = 2;

const InsuranceCard = props => {
  const {
    cardImage,
    showPlaceholder,
    scanStarted,
    scanCompleted,
    onClick,
    children,
    disabled,
    handleFileChange,
    parentControlAnimation,
    animationClassName,
    buttonStyle,
    placeholderCopy,
    placeholderImage,
  } = props;

  let view;
  if (cardImage) {
    view = VIEW_PICTURE;
  } else if (showPlaceholder) {
    view = VIEW_PLACEHOLDER;
  } else {
    view = VIEW_BLANK;
  }

  const showScanAnimation = scanStarted && !scanCompleted;

  switch (view) {
    case VIEW_PLACEHOLDER:
      return (
        <StyledCard
          sc={{ borderStyle: 'dashed', borderColor: colors.black }}
          elevation={0}
          onClick={onClick}
        >
          <StyledInput
            type={'file'}
            accept={'image/*'}
            onChange={handleFileChange}
            data-hj-suppress
          />

          <Container>
            <CardPlaceholder src={placeholderImage} />
            <HintContainer>
              <StyledCardHeader>{children}</StyledCardHeader>
              <StyledCardBody>{placeholderCopy}</StyledCardBody>
            </HintContainer>
          </Container>
          <Grid xs={12} item>
            <UploadButton variation={buttonStyle}>Take a photo</UploadButton>
          </Grid>
        </StyledCard>
      );
    case VIEW_PICTURE:
      return (
        <StyledCard sc={{ borderColor: 'none', disabled }} data-hj-suppress>
          {showScanAnimation && (
            <StyledScanHead
              className={animationClassName}
              sc={{ selfAnimate: !parentControlAnimation }}
            />
          )}
          <StyledCardPicture sc={{ imgData: cardImage }} data-hj-suppress>
            {showScanAnimation && (
              <StyledScanOverlay
                className={animationClassName}
                sc={{ selfAnimate: !parentControlAnimation }}
              />
            )}
          </StyledCardPicture>
        </StyledCard>
      );
    default:
    case VIEW_BLANK:
      return <StyledCard sc={{ borderStyle: 'solid', borderColor: colors.borderLine }} />;
  }
};

InsuranceCard.propTypes = {
  cardImage: PropTypes.string,
  children: PropTypes.any,
  onClick: PropTypes.func,
  showPlaceholder: PropTypes.bool,
  disabled: PropTypes.bool,
  scanStarted: PropTypes.bool,
  scanCompleted: PropTypes.bool,
  handleFileChange: PropTypes.func,
  parentControlAnimation: PropTypes.bool,
  animationClassName: PropTypes.string,
  buttonStyle: PropTypes.string,
};

InsuranceCard.defaultProps = {
  animationClassName: '',
  buttonStyle: ButtonVariations.pinkPrimary,
};

export default InsuranceCard;
